/**
 * Hive SSO client — OIDC Authorization Code + PKCE (S256), no dependencies.
 *
 * Feature flag: Hive mode is on iff VITE_HIVE_URL is set. When unset, this
 * module is inert and the app keeps its Supabase auth behavior byte-for-byte.
 *
 * Endpoints (Hive is a first-party IdP — paths are fixed, no discovery):
 *   GET  {HIVE_URL}/oidc/authorize   state + nonce + PKCE S256 all required
 *   POST {HIVE_URL}/oidc/token       form-encoded; refresh tokens ROTATE
 *   GET  {HIVE_URL}/oidc/logout      ?id_token_hint&post_logout_redirect_uri
 *
 * Storage:
 *   sessionStorage['athena.hive.txn']  in-flight login transaction
 *   localStorage ['athena.hive.auth']  session (access/refresh/id tokens)
 *
 * Refresh strategy: single-flight per tab, rotated refresh token persisted
 * BEFORE the flight resolves, `storage`-event adoption for other tabs, and a
 * proactive timer at exp−120s (± jitter) so tokens never go stale mid-use.
 */

const HIVE_URL = (import.meta.env.VITE_HIVE_URL ?? '').replace(/\/+$/, '');
const CLIENT_ID = import.meta.env.VITE_HIVE_CLIENT_ID ?? 'athena-web';

const TXN_KEY = 'athena.hive.txn';
const SESSION_KEY = 'athena.hive.auth';
const SCOPES = 'openid profile email org offline_access';

/** Refresh when a request wants a token and <60s of life remains. */
const REFRESH_SKEW_MS = 60_000;
/** Proactive background refresh fires 120s before expiry (± jitter). */
const PROACTIVE_LEAD_MS = 120_000;

export interface HiveUser {
  id: string;
  email?: string;
  name?: string;
}

export interface HiveSession {
  access_token: string;
  refresh_token?: string;
  id_token: string;
  /** Epoch ms when access_token expires. */
  expires_at: number;
  user: HiveUser;
}

interface LoginTxn {
  state: string;
  nonce: string;
  verifier: string;
  returnTo: string;
}

export function isHiveMode(): boolean {
  return Boolean(HIVE_URL);
}

function redirectUri(): string {
  return import.meta.env.VITE_HIVE_REDIRECT_URI ?? `${window.location.origin}/auth/callback`;
}

// --- tiny codecs -----------------------------------------------------------

function b64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecodeJson(segment: string): Record<string, unknown> {
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return JSON.parse(atob(padded)) as Record<string, unknown>;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) throw new Error('malformed JWT');
  return b64urlDecodeJson(parts[1]);
}

function randomToken(bytes = 32): string {
  return b64urlEncode(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function pkceChallengeS256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64urlEncode(new Uint8Array(digest));
}

// --- session store + change notification ------------------------------------

type Listener = (session: HiveSession | null) => void;
const listeners = new Set<Listener>();
let storageListenerAttached = false;

function emit(session: HiveSession | null): void {
  for (const cb of listeners) cb(session);
}

export function getHiveSession(): HiveSession | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as HiveSession;
    if (!s.access_token || !s.expires_at) return null;
    return s;
  } catch {
    return null;
  }
}

function setSession(session: HiveSession | null): void {
  if (session) window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else window.localStorage.removeItem(SESSION_KEY);
  scheduleProactiveRefresh();
  emit(session);
}

/**
 * Subscribe to session changes — local (login/refresh/sign-out) and cross-tab
 * (another tab rotated the refresh token; adopt its session instead of
 * racing). Returns an unsubscribe function.
 */
export function subscribeHiveSession(cb: Listener): () => void {
  listeners.add(cb);
  if (!storageListenerAttached) {
    storageListenerAttached = true;
    window.addEventListener('storage', (e) => {
      if (e.key !== SESSION_KEY) return;
      scheduleProactiveRefresh();
      emit(getHiveSession());
    });
  }
  return () => {
    listeners.delete(cb);
  };
}

// --- login ------------------------------------------------------------------

/**
 * Start the Authorization Code + PKCE flow. `returnTo` is restored after the
 * callback (defaults to /app).
 */
export async function beginLogin(returnTo = '/app'): Promise<void> {
  if (!isHiveMode()) return;

  // Canonical-origin guard: sessionStorage is per-origin, so a login started
  // on http://127.0.0.1:5175 could never read its txn back on the registered
  // http://localhost:5175/auth/callback. Hop to the canonical origin first.
  const canonicalOrigin = new URL(redirectUri()).origin;
  if (window.location.origin !== canonicalOrigin) {
    window.location.replace(
      canonicalOrigin + window.location.pathname + window.location.search + window.location.hash,
    );
    return;
  }

  const txn: LoginTxn = {
    state: randomToken(),
    nonce: randomToken(),
    verifier: randomToken(48),
    returnTo,
  };
  window.sessionStorage.setItem(TXN_KEY, JSON.stringify(txn));

  const url = new URL(`${HIVE_URL}/oidc/authorize`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', SCOPES);
  url.searchParams.set('state', txn.state);
  url.searchParams.set('nonce', txn.nonce);
  url.searchParams.set('code_challenge', await pkceChallengeS256(txn.verifier));
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope?: string;
  id_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
}

/** Thrown when the token endpoint answered. 4xx (`definitive`) means the
 * grant is dead (e.g. invalid_grant after refresh-token reuse); 5xx and
 * network failures are transient and must NOT sign the user out. */
class TokenEndpointError extends Error {
  constructor(
    message: string,
    public readonly definitive: boolean,
  ) {
    super(message);
  }
}

async function postToken(params: Record<string, string>): Promise<TokenResponse> {
  const res = await fetch(`${HIVE_URL}/oidc/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(params).toString(),
  });
  const body = (await res.json().catch(() => ({}))) as TokenResponse;
  if (!res.ok) {
    throw new TokenEndpointError(
      body.error_description ?? body.error ?? `token endpoint ${res.status}`,
      res.status >= 400 && res.status < 500,
    );
  }
  return body;
}

/**
 * Complete the flow on /auth/callback: validate state, exchange the code,
 * validate the ID-token nonce, persist the session. Returns the `returnTo`
 * path captured at beginLogin.
 */
export async function handleCallback(): Promise<string> {
  const params = new URLSearchParams(window.location.search);
  const oauthError = params.get('error');
  if (oauthError) {
    throw new Error(params.get('error_description') ?? oauthError);
  }
  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) throw new Error('missing code/state in callback URL');

  const rawTxn = window.sessionStorage.getItem(TXN_KEY);
  if (!rawTxn) throw new Error('no login in progress (transaction missing — retry sign-in)');
  const txn = JSON.parse(rawTxn) as LoginTxn;
  if (state !== txn.state) throw new Error('state mismatch — possible CSRF, sign-in aborted');

  const tokens = await postToken({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: CLIENT_ID,
    code_verifier: txn.verifier,
  });
  window.sessionStorage.removeItem(TXN_KEY);

  if (!tokens.id_token) throw new Error('token response missing id_token');
  const idClaims = decodeJwtPayload(tokens.id_token);
  if (idClaims.nonce !== txn.nonce) {
    throw new Error('nonce mismatch — sign-in aborted');
  }

  setSession({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    id_token: tokens.id_token,
    expires_at: Date.now() + tokens.expires_in * 1000,
    user: {
      id: String(idClaims.sub),
      email: typeof idClaims.email === 'string' ? idClaims.email : undefined,
      name: typeof idClaims.name === 'string' ? idClaims.name : undefined,
    },
  });

  return txn.returnTo || '/app';
}

// --- refresh ----------------------------------------------------------------

let refreshFlight: Promise<HiveSession | null> | null = null;

/**
 * Refresh the session (single-flight per tab). Hive ROTATES refresh tokens on
 * every use — the rotated token is persisted before this resolves, and other
 * tabs adopt it via the `storage` event instead of burning the old one.
 */
async function refreshSession(): Promise<HiveSession | null> {
  if (refreshFlight) return refreshFlight;
  refreshFlight = (async () => {
    const current = getHiveSession();
    if (!current?.refresh_token) return null;
    try {
      const tokens = await postToken({
        grant_type: 'refresh_token',
        refresh_token: current.refresh_token,
        client_id: CLIENT_ID,
      });
      const next: HiveSession = {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token ?? current.refresh_token,
        id_token: tokens.id_token ?? current.id_token,
        expires_at: Date.now() + tokens.expires_in * 1000,
        user: current.user,
      };
      setSession(next); // persist-before-resolve
      return next;
    } catch (err) {
      // The rotated token may have been consumed by another tab a moment ago —
      // adopt whatever that tab persisted before declaring the session dead.
      const adopted = getHiveSession();
      if (adopted && adopted.refresh_token !== current.refresh_token) return adopted;
      if (err instanceof TokenEndpointError && err.definitive) {
        // Grant is dead (revoked / reuse-detected / expired family) — sign out.
        setSession(null);
        return null;
      }
      // Transient (network / Hive 5xx): keep the session and retry shortly.
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        void refreshSession();
      }, 30_000);
      return adopted && adopted.expires_at > Date.now() ? adopted : null;
    }
  })().finally(() => {
    refreshFlight = null;
  });
  return refreshFlight;
}

/** Current access token, refreshing when <60s of life remains. Null = signed out. */
export async function getHiveAccessToken(): Promise<string | null> {
  if (!isHiveMode()) return null;
  const session = getHiveSession();
  if (!session) return null;
  if (session.expires_at - Date.now() > REFRESH_SKEW_MS) return session.access_token;
  const refreshed = await refreshSession();
  return refreshed?.access_token ?? null;
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/** (Re)arm the background refresh timer at exp−120s ± 15s jitter. */
export function scheduleProactiveRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  const session = getHiveSession();
  if (!session?.refresh_token) return;
  const jitter = Math.floor(Math.random() * 15_000);
  const delay = Math.max(session.expires_at - Date.now() - PROACTIVE_LEAD_MS + jitter, 5_000);
  refreshTimer = setTimeout(() => {
    void refreshSession();
  }, delay);
}

// --- sign-out ---------------------------------------------------------------

/** Clear the local session and end the Hive SSO session (full redirect). */
export function signOutHive(): void {
  const session = getHiveSession();
  setSession(null);
  const url = new URL(`${HIVE_URL}/oidc/logout`);
  if (session?.id_token) url.searchParams.set('id_token_hint', session.id_token);
  url.searchParams.set('post_logout_redirect_uri', `${window.location.origin}/`);
  window.location.assign(url.toString());
}
